import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { generatePath, matchPath, useHistory } from 'react-router-dom';

import {
  InfoWorkType,
  Job,
  JobCompanyInfo,
} from '@kanda-libs/ks-frontend-services';

import { URLS } from 'config';
import { useRefreshingData } from 'hooks';
import { selectors } from 'store';
import {
  GroupedByAprAndDuration,
  checkHasSecondLine,
  getCurrentInfoWorkType,
  getValidFinanceRates,
} from 'utils/FinanceRate-functions';

export type JobPage = 'QUOTE' | 'CHECKOUT' | 'COMPLETE';

export type JobPageType = 'JOB' | 'REAPPLY';

export type PaymentType = 'OPEN_BANKING' | 'FINANCE';

export type ChangesStatus = 'REQUESTED' | 'REVIEW';

export interface JobContextType {
  job: Job | undefined;
  jobCompanyInfo: JobCompanyInfo | undefined;
  infoWorkType: InfoWorkType | undefined;
  page: JobPage;
  companyName: string;
  goToPage: (page: JobPage) => void;
  isLoading: boolean;
  showCardPayment: boolean;
  paymentType: PaymentType | undefined;
  setPaymentType: (newPaymentType?: PaymentType) => void;
  changesStatus: ChangesStatus | undefined;
  isIntroduced: boolean;
  isExampleJob: boolean;
  allRates: GroupedByAprAndDuration | undefined;
  showOpenBankingPayment: boolean;
  formRef: React.MutableRefObject<HTMLDivElement | null>;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const JobContext = createContext<JobContextType | null>(null);

export const useJobContext = () => useContext(JobContext) as JobContextType;

export interface JobProviderProps {
  children: ReactNode;
}

const getJobBaseURL = (job: Job): string | undefined => {
  const hasSecondLine = checkHasSecondLine(job);
  // User has attempted reapply
  if (
    job.status === 'sent' &&
    job.finance_status === 'finance_not_approved' &&
    hasSecondLine
  ) {
    return URLS.reapply;
  }
  if (
    (job.status === 'sent' || job.status === 'draft') &&
    job.finance_status === 'not_applied'
  )
    return undefined;
  return URLS.status;
};

const getReapplyBaseURL = (job: Job): string | undefined => {
  const hasSecondLine = checkHasSecondLine(job);
  if (!hasSecondLine) return URLS.job;
  if (job.status === 'sent') return undefined;
  return URLS.status;
};

const getBaseURL = (
  job: Job | undefined,
  type: JobPageType,
): string | undefined => {
  if (!job) return undefined;
  if (job?.quoted_to) return URLS.quote;
  if (type === 'JOB') return getJobBaseURL(job);
  if (type === 'REAPPLY') return getReapplyBaseURL(job);
  return undefined;
};

const isValidJobPageState = (state: unknown): state is { page: JobPage } =>
  state !== null &&
  typeof state === 'object' &&
  'page' in state &&
  (state.page === 'QUOTE' ||
    state.page === 'CHECKOUT' ||
    state.page === 'SUMMARY');

const JobProvider: FunctionComponent<JobProviderProps> = function ({
  children,
}) {
  const history = useHistory();

  const [paymentType, setPaymentType] = useState<PaymentType | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [page, setPage] = useState<JobPage>(() => {
    const routerState = history.location.state;
    if (isValidJobPageState(routerState)) {
      return routerState.page;
    }
    return 'QUOTE';
  });

  const { cleanUp } = useRefreshingData(30000);

  const job = useSelector(selectors.job.getEntity);
  const jobHasFetched = useSelector(selectors.job.getHasFetched);
  const jobIsLoading = useMemo(
    () => !job || !jobHasFetched,
    [job, jobHasFetched],
  );

  const jobCompanyInfo = useSelector(selectors.jobCompanyInfo.getEntity);
  const jobCompanyInfoHasFetched = useSelector(
    selectors.jobCompanyInfo.getHasFetched,
  );
  const jobCompanyInfoIsLoading = useMemo(
    () => !jobCompanyInfo || !jobCompanyInfoHasFetched,
    [jobCompanyInfo, jobCompanyInfoHasFetched],
  );

  const infoWorkTypes = useSelector(selectors.infoWorkType.getRawResponse) as
    | InfoWorkType[]
    | undefined;
  const infoWorkTypeHasFetched = useSelector(
    selectors.infoWorkType.getHasFetched,
  );
  const infoWorkTypeIsLoading = useMemo(
    () => !infoWorkTypes || !infoWorkTypeHasFetched,
    [infoWorkTypes, infoWorkTypeHasFetched],
  );

  const isIntroduced = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.introduced_to);
  }, [job]);

  const isExampleJob = useMemo(() => job?.flow_type === 'example', [job]);

  const infoWorkType = useMemo(
    () => getCurrentInfoWorkType(job, infoWorkTypes),
    [job, infoWorkTypes],
  );

  const allRates = useMemo(
    () => getValidFinanceRates(job, infoWorkType, isExampleJob),
    [infoWorkType, job, isExampleJob],
  );

  const showOpenBankingPayment = useMemo(() => {
    if (!job || !jobCompanyInfo) return false;
    if (jobCompanyInfo.hide_openbanking) return false;
    return Boolean(job.checkout_options?.includes('pay_by_bank'));
  }, [job, jobCompanyInfo]);

  const showCardPayment = useMemo(() => {
    if (!job) return false;
    return Boolean(job.checkout_options?.includes('pay_on_card'));
  }, [job]);

  const {
    push,
    location: { pathname },
  } = useHistory();

  const isLoading = useMemo(
    () => jobIsLoading || jobCompanyInfoIsLoading || infoWorkTypeIsLoading,
    [jobIsLoading, jobCompanyInfoIsLoading, infoWorkTypeIsLoading],
  );

  const jobPageType: JobPageType = useMemo(
    () =>
      matchPath(pathname, {
        path: URLS.reapply,
        exact: true,
        strict: false,
      })
        ? 'REAPPLY'
        : 'JOB',
    [pathname],
  );

  const formRef = useRef<HTMLDivElement | null>(null);

  const goToPage = useCallback((newPage: JobPage) => {
    setPage(newPage);
  }, []);

  const updatePaymentType = useCallback((newPaymentType?: PaymentType) => {
    setPaymentType(newPaymentType);
    if (!newPaymentType) return;
    setTimeout(() => {
      const el = formRef.current;
      if (!el) return;
      const offset = window.innerWidth < 768 ? 122 : 65;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({
        top: y,
        behavior: 'smooth',
      });
    }, 100);
  }, []);

  const companyName = useMemo(() => {
    if (isLoading) return 'your tradesperson';
    return jobCompanyInfo?.company_name ?? 'your tradesperson';
  }, [isLoading, jobCompanyInfo]);

  const changesStatus: ChangesStatus | undefined = useMemo(() => {
    if (!job) return undefined;
    const { current_step: currentStep } = job;
    if (!currentStep) return undefined;
    if (currentStep.name === 'checkout_quote_changes_requested')
      return 'REQUESTED';
    if (currentStep.name === 'checkout_quote_changes_amended') return 'REVIEW';
    return undefined;
  }, [job]);

  const values = useMemo(
    () => ({
      job,
      jobCompanyInfo,
      infoWorkType,
      page,
      companyName,
      isLoading,
      goToPage,
      showCardPayment,
      paymentType,
      setPaymentType: updatePaymentType,
      // TODO: implement from Job when review implemented in API
      changesStatus,
      isIntroduced,
      isExampleJob,
      allRates,
      showOpenBankingPayment,
      formRef,
      isSubmitting,
      setIsSubmitting,
    }),
    [
      job,
      infoWorkType,
      jobCompanyInfo,
      isLoading,
      showCardPayment,
      page,
      paymentType,
      isIntroduced,
      isExampleJob,
      allRates,
      showOpenBankingPayment,
      formRef,
      goToPage,
      updatePaymentType,
      isSubmitting,
      setIsSubmitting,
      changesStatus,
      companyName,
    ],
  );

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (initRef.current || !job) return;
    if (job?.status === 'declined') setPage('COMPLETE');
    initRef.current = true;
  }, [job]);

  useEffect(() => {
    const id = job?.id;
    if (!job || !id) return;
    const baseURL = getBaseURL(job, jobPageType);
    if (!baseURL) return;
    push(generatePath(baseURL, { id }));
  }, [job, jobPageType, push]);

  useEffect(() => () => cleanUp(), [cleanUp]);

  useEffect(() => {
    if (history.location.state) {
      history.replace({
        pathname: history.location.pathname,
        state: undefined, // Clears the router state
      });
    }
  }, [history]);

  return <JobContext.Provider value={values}>{children}</JobContext.Provider>;
};

export default JobProvider;
